// Services
import store from '../store'

// Utilities
import environment from './environmentUtils'
import isEmpty from './isEmpty'
import logger from './loggerUtil'
import { AnalyticsConstants } from '../constants'

class SessionRecordUtil {
  /**
   * Sets the user ID in session recording integration.
   * @param {String|Number} identifier - The user specific variable to save (profile ID, email, phone number, appliation ID, customer ID, etc.).
   * @param {String} [prefix] - Optionally a prefix to include before the identifier value to differentiate from other types of identifiers.
   */
  static setUserIdentifier (identifier, prefix = undefined) {
    try {
      prefix = isEmpty(prefix) ? '' : `${prefix}_`

      const idValue = `${prefix}${identifier}`

      logger.debug(`Setting session record user identifier ${idValue}`)

      window._uxa = window._uxa || []

      window._uxa.push(['trackPageEvent', `@user-identifier@${idValue}`])
    } catch (error) {
      logger.error('Error setting session record user identifier')
      logger.debug(error)
    }
  }

  /**
   * Sets the user property in session recording integration. 40 distinct variables allowed per page view.
   * @param {String} key - The user property name (512 character max).
   * @param {String|Number} value - The user property value (255 character max).
   */
  static setUserProperty (key, value) {
    try {
      window._uxa = window._uxa || []

      window._uxa.push(['trackDynamicVariable', { key, value }])
    } catch (error) {
      logger.error('Error setting session record user info')
      logger.debug(error)
    }
  }

  /**
   * Sets user identity user properties after authentication.
   */
  static updateIdentity () {
    try {
      window._uxa = window._uxa || []

      const nlsCustomerId = store.getters['customer/nlsCustomerId']
      const vergentCustomerId = store.getters['customer/tlxeCustomerId']
      const cognitoId = store.getters['session/getCognitoId']

      SessionRecordUtil.setUserIdentifier(cognitoId)

      SessionRecordUtil.saveInitUserProperties()

      if (!isEmpty(vergentCustomerId)) {
        SessionRecordUtil.setUserIdentifier(vergentCustomerId, AnalyticsConstants.propName.mobileTLXeCustomerId)
      }

      if (!isEmpty(nlsCustomerId)) {
        SessionRecordUtil.setUserIdentifier(nlsCustomerId, AnalyticsConstants.propName.mobileNLSCustomerId)
      }

      SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.isMobileGenerated, 'true')
    } catch (error) {
      logger.error('Error setting update identity session record user properties')
      logger.debug(error)
    }
  }

  /**
   * Sets the site-wide, initialization related user properties.
   */
  static saveInitUserProperties () {
    SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.environment, environment.environment)
    SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.siteBrand, process.env.VUE_APP_AppBrand)

    if (store.state.display.isPwa) {
      SessionRecordUtil.setUserProperty(true, AnalyticsConstants.propName.isPwa)
    }
  }

  /**
   * Records a single artificial page view into session recording integration. The integration automatically tracks full page reloads which do not happen in SPAs.
   */
  static logPageView () {
    try {
      window._uxa = window._uxa || []
      window._uxa.push(['trackPageview', `${window.location.pathname}${window.location.hash.replace('#', 'csp')}`]) // Prevent CSP from stripping off path names after hash mark
    } catch (error) {
      logger.error('Error logging session record page view event')
      logger.debug(error)
    }
  }
}

export default SessionRecordUtil
